import React from "react";
import { Box, Typography } from "@mui/material";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";

/**
 * Small presentational pieces for the flow-style automation builder.
 * No data logic here — see automations/automationStorage for that.
 */

export const PRIMARY = "var(--portal-theme-primary)";
export const PRIMARY_SOFT =
  "color-mix(in srgb, var(--portal-theme-primary) 9%, white)";
export const PRIMARY_RING =
  "color-mix(in srgb, var(--portal-theme-primary) 16%, transparent)";
export const PRIMARY_HOVER =
  "color-mix(in srgb, var(--portal-theme-primary) 50%, white)";
export const BORDER = "#e2e8f0";
export const LINE = "#cbd5e1";
export const MUTED = "#64748b";
export const INK = "#0f172a";

export const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    bgcolor: "#fff",
    fontSize: 14,
  },
};

export const Panel = ({ children, sx = {} }) => (
  <Box
    sx={{
      bgcolor: "#fff",
      border: `1px solid ${BORDER}`,
      borderRadius: "12px",
      ...sx,
    }}
  >
    {children}
  </Box>
);

/** Card with an icon header — one per step in the flow. */
export const StepCard = ({ icon, tone = "primary", title, children }) => {
  const warm = tone === "warm";
  return (
    <Panel sx={{ overflow: "hidden" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          px: 2,
          py: 1.5,
          borderBottom: `1px solid ${BORDER}`,
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: "8px",
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
            bgcolor: warm ? "#fef3c7" : PRIMARY_SOFT,
            color: warm ? "#b45309" : PRIMARY,
          }}
        >
          {icon}
        </Box>
        <Typography sx={{ fontWeight: 700, fontSize: 15, color: INK }}>
          {title}
        </Typography>
      </Box>
      <Box sx={{ p: { xs: 1.75, sm: 2 } }}>{children}</Box>
    </Panel>
  );
};

export const FieldLabel = ({ children, htmlFor, sx = {} }) => (
  <Typography
    component={htmlFor ? "label" : "p"}
    htmlFor={htmlFor}
    sx={{
      display: "block",
      fontSize: 13,
      fontWeight: 700,
      color: INK,
      mb: 0.75,
      ...sx,
    }}
  >
    {children}
  </Typography>
);

export const ErrorText = ({ children, sx = {} }) => (
  <Typography sx={{ fontSize: 12, color: "#dc2626", mt: 0.75, ...sx }}>
    {children}
  </Typography>
);

/** Selectable card — used for trigger model, trigger and action. */
export const OptionTile = ({ selected, title, icon, onClick }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    aria-pressed={Boolean(selected)}
    sx={{
      width: "100%",
      height: "100%",
      display: "flex",
      alignItems: "center",
      gap: 1,
      px: 1.5,
      py: 1.25,
      textAlign: "left",
      fontFamily: "inherit",
      fontSize: 13.5,
      fontWeight: 700,
      lineHeight: 1.3,
      color: INK,
      cursor: "pointer",
      borderRadius: "10px",
      border: "1.5px solid",
      borderColor: selected ? PRIMARY : BORDER,
      bgcolor: selected ? PRIMARY_SOFT : "#fff",
      boxShadow: selected ? `0 0 0 3px ${PRIMARY_RING}` : "none",
      transition: "border-color .15s ease, background-color .15s ease",
      "&:hover": { borderColor: selected ? PRIMARY : PRIMARY_HOVER },
      "&:focus-visible": { outline: `2px solid ${PRIMARY}`, outlineOffset: 2 },
    }}
  >
    {icon ? (
      <Box
        component="span"
        sx={{
          display: "flex",
          color: selected ? PRIMARY : MUTED,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
    ) : null}
    <Box component="span" sx={{ minWidth: 0 }}>
      {title}
    </Box>
  </Box>
);

/** Row of pill buttons — pick one value. */
export const PillGroup = ({ options, value, onChange, size, ariaLabel }) => {
  const small = size === "small";
  return (
    <Box
      role="radiogroup"
      aria-label={ariaLabel}
      sx={{ display: "flex", flexWrap: "wrap", gap: small ? 0.5 : 0.75 }}
    >
      {options.map((option) => {
        const active = option.value === value;
        return (
          <Box
            key={String(option.value)}
            component="button"
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(option.value)}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              px: small ? 1.25 : 1.75,
              py: small ? 0.35 : 0.65,
              fontFamily: "inherit",
              fontSize: small ? 12 : 13,
              fontWeight: 600,
              cursor: "pointer",
              borderRadius: "999px",
              border: "1px solid",
              borderColor: active ? PRIMARY : BORDER,
              bgcolor: active ? PRIMARY : "#fff",
              color: active ? "#fff" : "#334155",
              transition: "background-color .15s ease, border-color .15s ease",
              "&:hover": { borderColor: PRIMARY },
              "&:focus-visible": {
                outline: `2px solid ${PRIMARY}`,
                outlineOffset: 2,
              },
            }}
          >
            {option.label}
          </Box>
        );
      })}
    </Box>
  );
};

/** Vertical line + arrow between two steps. */
export const Connector = () => (
  <Box
    aria-hidden
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      py: 0.25,
    }}
  >
    <Box sx={{ width: 2, height: 16, bgcolor: LINE }} />
    <Box
      sx={{
        width: 28,
        height: 28,
        borderRadius: "50%",
        display: "grid",
        placeItems: "center",
        bgcolor: PRIMARY,
        color: "#fff",
      }}
    >
      <ArrowDownwardRoundedIcon sx={{ fontSize: 17 }} />
    </Box>
    <Box sx={{ width: 2, height: 16, bgcolor: LINE }} />
  </Box>
);

export const EndMarker = () => (
  <Box
    aria-hidden
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      pt: 0.25,
    }}
  >
    <Box sx={{ width: 2, height: 20, bgcolor: LINE }} />
    <Typography
      sx={{
        mt: 0.5,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 1.2,
        color: "#94a3b8",
      }}
    >
      END
    </Typography>
  </Box>
);
